import { FC, Dispatch, SetStateAction, MutableRefObject } from "react";
import {
  Box,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import ReplayIcon from "@mui/icons-material/Replay";
import LockResetIcon from "@mui/icons-material/LockReset";
import DropDown from "@/components/forms/inputs/dropDown";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { MuiTelInput } from "mui-tel-input";
import { api } from "@/helper/api";
import { emailValidationRegex } from "@/helper/regex";
import { useTranslation } from "react-i18next";
import styles from "@/components/anchorDataOverview/anchorDataOverview.module.css";
import AnchorDataOverviewDatePicker from "@/components/anchorDataOverview/anchorDataOverviewDatePicker";
import { getAnchorDataBulkCreateUpdateErrors } from "@/helper/anchorDataBulkCreateUpdate.utils";
import {
  normalizePastedPhoneNumber,
  inheritedValueToSubjectIdListOptions,
  inheritedValueToWzCodeIdListOptions,
  blurNumberInputOnWheel,
} from "@/components/anchorDataOverview/anchorDataOverviewUtils";
import AnchorDataOverviewFieldEditor from "@/components/anchorDataOverview/anchorDataOverviewFieldEditor";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

type AnchorDataOverviewModalProps = {
  open: boolean;
  selectedRow: any;
  setSelectedRow: Dispatch<SetStateAction<any>>;
  setEditModalOpen: Dispatch<SetStateAction<boolean>>;
  selectedAnchorDataTemplates: (any | null)[];
  anchorDataTemplateTypes: Record<string, string>;
  anchorDataTemplateDetails: Record<string, any>;
  anchorDataValues: Record<string, any>;
  setAnchorDataValues: Dispatch<SetStateAction<Record<string, any>>>;
  valueMaps: Record<string, any[]>;
  setValueMaps: Dispatch<SetStateAction<Record<string, any[]>>>;
  typeMappings: Record<string, any>;
  setTypeMappings: Dispatch<SetStateAction<Record<string, any>>>;
  objectListFields: Record<string, any[]>;
  setObjectListFields: Dispatch<SetStateAction<Record<string, any[]>>>;
  fieldErrors: Record<string, string>;
  setFieldErrors: Dispatch<SetStateAction<Record<string, string>>>;
  resetToInheritedTemplates: Record<string, boolean>;
  setResetToInheritedTemplates: Dispatch<
    SetStateAction<Record<string, boolean>>
  >;
  inheritedResetMeta: Record<
    string,
    { parentName: string; inheritedValue: any }
  >;
  setInheritedResetMeta: Dispatch<
    SetStateAction<Record<string, { parentName: string; inheritedValue: any }>>
  >;
  isSubjectRootOfTree: boolean;
  setIsSubjectRootOfTree: Dispatch<SetStateAction<boolean>>;
  setRefreshTable: Dispatch<SetStateAction<boolean>>;
  inputRefs: MutableRefObject<Record<string, HTMLInputElement | null>>;
  fetchSubjects: any;
  kaerCompanyId: string;
  setError: Dispatch<SetStateAction<any>>;
  isSameAnchorDataValue: (
    nextValue: any,
    initialValue: any,
    type: string,
  ) => boolean;
  formatInheritedForDisplay: (value: any, type: string) => string;
};

const AnchorDataOverviewModal: FC<AnchorDataOverviewModalProps> = ({
  open,
  selectedRow,
  setSelectedRow,
  setEditModalOpen,
  selectedAnchorDataTemplates,
  anchorDataTemplateTypes,
  anchorDataTemplateDetails,
  anchorDataValues,
  setAnchorDataValues,
  valueMaps,
  setValueMaps,
  typeMappings,
  setTypeMappings,
  objectListFields,
  setObjectListFields,
  fieldErrors,
  setFieldErrors,
  resetToInheritedTemplates,
  setResetToInheritedTemplates,
  inheritedResetMeta,
  setInheritedResetMeta,
  isSubjectRootOfTree,
  setIsSubjectRootOfTree,
  setRefreshTable,
  inputRefs,
  fetchSubjects,
  kaerCompanyId,
  setError,
  isSameAnchorDataValue,
  formatInheritedForDisplay,
}) => {
  const { t } = useTranslation();
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, string>({
      fieldOrder: [],
    });

  const handleEditSubmit = async () => {
    if (!selectedRow) return;

    const nextFieldErrors = { ...fieldErrors };
    selectedAnchorDataTemplates.filter(Boolean).forEach((template) => {
      const type = anchorDataTemplateTypes[template.value];
      if (type === "email") {
        delete nextFieldErrors[template.value];
        const emailValue = inputRefs.current[template.value]?.value ?? "";
        if (emailValue && !emailValidationRegex.test(emailValue)) {
          nextFieldErrors[template.value] = t("enter_valid_email");
        }
      }
    });

    const invalidTemplateIds = selectedAnchorDataTemplates
      .filter(Boolean)
      .map((template) => String(template.value))
      .filter((templateId) => Boolean(nextFieldErrors[templateId]));
    if (invalidTemplateIds.length > 0) {
      setFieldErrors(nextFieldErrors);
      focusFirstInvalidField({}, invalidTemplateIds);
      return;
    }

    try {
      const anchorDataValuesPayload: Record<string, any> = {};

      for (const template of selectedAnchorDataTemplates.filter(Boolean)) {
        const templateId = template.value;
        const templateName =
          anchorDataTemplateDetails[templateId]?.name ?? templateId;
        const type = anchorDataTemplateTypes[templateId];
        const initialValue = selectedRow?.[`value_${templateId}`];
        const inheritedValueForReset =
          inheritedResetMeta[templateId]?.inheritedValue ??
          selectedRow?.[`inherited_value_${templateId}`];
        let finalValue: any;

        if (type === "enum") {
          const value = anchorDataValues[templateId];
          finalValue = value === "" || value == null ? null : String(value);
        } else if (type === "int") {
          const raw = inputRefs.current[templateId]?.value ?? "";
          finalValue = raw === "" ? null : raw ? parseInt(raw, 10) : null;
        } else if (type === "float") {
          const raw = inputRefs.current[templateId]?.value ?? "";
          if (raw === "") {
            finalValue = null;
          } else {
            const normalized = raw.replace(",", ".");
            const parsed = parseFloat(normalized);
            finalValue = Number.isNaN(parsed) ? null : parsed;
          }
        } else if (type === "percent") {
          const raw = inputRefs.current[templateId]?.value ?? "";
          if (raw === "") {
            finalValue = null;
          } else {
            const parsed = parseFloat(String(raw).replace(",", "."));
            finalValue = Number.isNaN(parsed) ? null : parsed;
          }
        } else if (type === "date") {
          const dateValue =
            anchorDataValues[templateId] !== undefined
              ? anchorDataValues[templateId]
              : initialValue;
          finalValue =
            dateValue === "" || dateValue == null ? null : String(dateValue);
        } else if (type === "subject_id_list") {
          const value = anchorDataValues[templateId];
          if (Array.isArray(value) && value.length > 0) {
            finalValue = value.map((v) =>
              typeof v === "object"
                ? v?.value ?? v?.id ?? String(v)
                : String(v),
            );
          } else {
            finalValue = [];
          }
        } else if (type === "wz_code_id_list") {
          const value = anchorDataValues[templateId];
          if (Array.isArray(value) && value.length > 0) {
            finalValue = value.map((v) =>
              typeof v === "object"
                ? v?.value ?? v?.id ?? String(v)
                : String(v),
            );
          } else {
            finalValue = [];
          }
        } else if (type === "object_list") {
          const objectListData = objectListFields[templateId] || [];
          const allowedValueSet = valueMaps[templateId] || [];
          const typeMapping = typeMappings[templateId] || {};

          const normalizeToNull = (v: any) =>
            v === "" || v === undefined || v === null ? null : v;

          finalValue = objectListData
            .map((obj: any) => {
              const result: any = {};

              allowedValueSet.forEach((field: any) => {
                const fieldName =
                  typeof field === "string" ? field : field.value;
                const fieldType = typeMapping[fieldName] || "str";
                const rawValue = normalizeToNull(obj?.[fieldName]);

                if (rawValue === null) {
                  result[fieldName] = null;
                  return;
                }

                if (fieldType === "int") {
                  const parsed = parseInt(String(rawValue), 10);
                  result[fieldName] = Number.isNaN(parsed) ? null : parsed;
                } else if (fieldType === "float") {
                  const normalized = String(rawValue).replace(",", ".");
                  const parsed = parseFloat(normalized);
                  result[fieldName] = Number.isNaN(parsed) ? null : parsed;
                } else {
                  result[fieldName] = String(rawValue);
                }
              });

              return result;
            })
            .filter((obj: any) =>
              Object.values(obj).some((v) => v !== null && v !== ""),
            );
        } else if (type === "phone_number") {
          const value = anchorDataValues[templateId];
          finalValue = value === "" || value == null ? null : String(value);
        } else if (type === "str") {
          const value = anchorDataValues[templateId];
          const raw = inputRefs.current[templateId]?.value ?? "";
          if (value === "") {
            finalValue = "";
          } else if (raw === "") {
            const initialWasEmpty =
              initialValue === "" ||
              (typeof initialValue === "number" && Number.isNaN(initialValue));
            finalValue = initialWasEmpty ? "" : null;
          } else {
            finalValue = raw;
          }
        } else {
          const raw = inputRefs.current[templateId]?.value ?? "";
          finalValue = raw === "" ? null : raw;
        }

        const isEmptyList =
          (type === "subject_id_list" || type === "wz_code_id_list") &&
          (finalValue == null ||
            (Array.isArray(finalValue) && finalValue.length === 0));
        const shouldSubmitAsInherited =
          resetToInheritedTemplates[templateId] &&
          (inheritedValueForReset == null
            ? isEmptyList
            : isSameAnchorDataValue(finalValue, inheritedValueForReset, type));

        if (shouldSubmitAsInherited) {
          finalValue = null;
        }

        if (isSameAnchorDataValue(finalValue, initialValue, type)) {
          continue;
        }

        anchorDataValuesPayload[templateName] =
          finalValue === null ? null : { value: finalValue };
      }

      if (Object.keys(anchorDataValuesPayload).length > 0) {
        const res: any = await new Promise<any>((resolve, reject) => {
          api.genericApiRequest({
            method: "post",
            entity: "anchorData",
            additionalRouteParts: { bulk_create_update: "/" },
            parametersToRender: {},
            submitData: [{
              subject: selectedRow.id,
              anchor_data_values: anchorDataValuesPayload,
            }],
            successHandler: (res: any) => resolve(res),
            failHandler: (err: any) => reject(err),
          });
        });
        const bulkErrors = getAnchorDataBulkCreateUpdateErrors(res, {
          submittedSubjectIds: [selectedRow.id],
          subjectNameById: new Map([[selectedRow.id, selectedRow.name]]),
        });
        if (bulkErrors.length > 0) {
          throw bulkErrors;
        }
      }

      setEditModalOpen(false);
      setSelectedRow(null);
      setAnchorDataValues({});
      setValueMaps({});
      setResetToInheritedTemplates({});
      setInheritedResetMeta({});
      setIsSubjectRootOfTree(false);

      setRefreshTable((prev) => !prev);
    } catch (err) {
      setError(err);
    }
  };
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: handleEditSubmit,
  });

  const renderValueInput = (
    template: any,
    useInheritedValueStyling = false,
  ) => {
    const type = anchorDataTemplateTypes[template.value];
    const value = anchorDataValues[template.value];
    const valueMap = valueMaps[template.value] || [];
    const inheritedInputSx = useInheritedValueStyling
      ? {
          "& .MuiInputBase-input": {
            color: "var(--newWork60)",
            fontStyle: "italic",
          },
        }
      : undefined;
    const inheritedSelectStyles = useInheritedValueStyling
      ? {
          singleValue: (baseStyles: any) => ({
            ...baseStyles,
            color: "var(--newWork60)",
            fontStyle: "italic",
          }),
        }
      : undefined;

    const dynKey = `value_${template.value}`;
    const initial = selectedRow?.[dynKey] ?? "";

    switch (type) {
      case "int":
        return (
          <TextField
            fullWidth
            type="number"
            inputProps={{ onWheel: blurNumberInputOnWheel }}
            value={(value !== undefined ? value : initial) ?? ""}
            inputRef={(el) => {
              inputRefs.current[template.value] = el;
            }}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = raw === "" ? null : parseInt(raw, 10);
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: Number.isNaN(parsed as number)
                  ? null
                  : parsed,
              }));
            }}
            sx={inheritedInputSx}
            autoFocus
          />
        );
      case "float": {
        const floatDefaultValue = initial ?? "";
        return (
          <TextField
            fullWidth
            type="number"
            inputProps={{ step: "any", onWheel: blurNumberInputOnWheel }}
            value={(value !== undefined ? value : floatDefaultValue) ?? ""}
            inputRef={(el) => {
              inputRefs.current[template.value] = el;
            }}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                setAnchorDataValues((prev) => ({
                  ...prev,
                  [template.value]: null,
                }));
                return;
              }
              const parsed = parseFloat(raw.replace(",", "."));
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: Number.isNaN(parsed) ? null : parsed,
              }));
            }}
            sx={inheritedInputSx}
            autoFocus
          />
        );
      }
      case "percent": {
        const percentDefaultValue =
          (value !== undefined ? value : initial) ?? "";
        return (
          <TextField
            fullWidth
            type="number"
            inputProps={{
              step: "0.01",
              min: 0,
              max: 100,
              onWheel: blurNumberInputOnWheel,
            }}
            value={percentDefaultValue}
            inputRef={(el) => {
              inputRefs.current[template.value] = el;
            }}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = parseFloat(String(raw).replace(",", "."));
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]:
                  raw === "" || Number.isNaN(parsed) ? null : parsed,
              }));
            }}
            sx={inheritedInputSx}
            autoFocus
          />
        );
      }
      case "date": {
        const dateValue = value !== undefined ? value : initial;
        return (
          <AnchorDataOverviewDatePicker
            value={
              dateValue == null || dateValue === "" ? null : String(dateValue)
            }
            onChange={(newValue) => {
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: newValue,
              }));
            }}
            sx={inheritedInputSx}
            autoFocus
          />
        );
      }
      case "enum": {
        const enumOptions = Array.isArray(valueMap)
          ? valueMap.map((item: any) => {
              if (typeof item === "string") {
                return { value: item, label: item };
              }
              if (
                item &&
                typeof item === "object" &&
                "value" in item &&
                "label" in item
              ) {
                return { value: String(item.value), label: String(item.label) };
              }
              const [label, val] = Object.entries(item)[0] || [item, item];
              return { value: String(val), label: String(label) };
            })
          : [];

        if (enumOptions.length === 0) {
          return (
            <Box className={styles.noOptionsAvailableContainer}>
              <Box className={styles.noOptionsAvailableTextField}>
                <TextField
                  fullWidth
                  value={value ? String(value) : ""}
                  disabled
                />
                <ClearIcon
                  onClick={() =>
                    setAnchorDataValues((prev) => ({
                      ...prev,
                      [template.value]: "",
                    }))
                  }
                  className={styles.clearIcon}
                />
              </Box>
              <Typography className={styles.noOptionsAvailableText}>
                {t("no_options_available")}
              </Typography>
            </Box>
          );
        }

        return (
          <DropDown
            options={enumOptions}
            defaultValue={
              value !== "" && value != null ? String(value) : undefined
            }
            onChange={(sel) =>
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: sel ? sel.value : "",
              }))
            }
            isClearable
            isSearchable
            customStyles={inheritedSelectStyles}
          />
        );
      }
      case "multiline":
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={(value !== undefined ? value : initial) ?? ""}
            inputRef={(el) => {
              inputRefs.current[template.value] = el;
            }}
            onChange={(e) => {
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: e.target.value,
              }));
            }}
            sx={inheritedInputSx}
            autoFocus
          />
        );
      case "email": {
        const fieldError = fieldErrors[template.value];
        return (
          <TextField
            fullWidth
            type="email"
            value={(value !== undefined ? value : initial) ?? ""}
            inputRef={(el) => {
              inputRefs.current[template.value] = el;
            }}
            error={!!fieldError}
            helperText={fieldError || ""}
            onChange={(e) => {
              const emailValue = e.target.value;
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: emailValue === "" ? null : emailValue,
              }));
              if (!emailValue) {
                setFieldErrors((prev) => {
                  if (!prev[template.value]) return prev;
                  const next = { ...prev };
                  delete next[template.value];
                  return next;
                });
                return;
              }

              if (emailValidationRegex.test(emailValue)) {
                setFieldErrors((prev) => {
                  if (!prev[template.value]) return prev;
                  const next = { ...prev };
                  delete next[template.value];
                  return next;
                });
              } else {
                setFieldErrors((prev) => ({
                  ...prev,
                  [template.value]: t("enter_valid_email"),
                }));
              }
            }}
            onBlur={(e) => {
              const emailValue = e.target.value;
              if (emailValue && !emailValidationRegex.test(emailValue)) {
                setFieldErrors((prev) => ({
                  ...prev,
                  [template.value]: t("enter_valid_email"),
                }));
              } else {
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next[template.value];
                  return next;
                });
              }
            }}
            sx={inheritedInputSx}
            autoFocus
          />
        );
      }
      case "phone_number": {
        const phoneValue = anchorDataValues[template.value] ?? initial ?? "";
        return (
          <FormControlLabel
            label=""
            labelPlacement="top"
            disableTypography
            sx={{
              margin: "0",
              width: "100%",
              alignItems: "start",
              fontFamily: "inherit !important",
            }}
            control={
              <MuiTelInput
                placeholder={t("input_your_phone_number")}
                defaultCountry="DE"
                langOfCountryName="DE"
                value={phoneValue}
                onChange={(newValue) => {
                  setAnchorDataValues((prev) => ({
                    ...prev,
                    [template.value]: newValue,
                  }));
                }}
                onPaste={(e: React.ClipboardEvent) => {
                  const text =
                    e.clipboardData?.getData?.("text/plain") ??
                    e.clipboardData?.getData?.("text") ??
                    "";
                  const normalized = normalizePastedPhoneNumber(text);
                  if (normalized !== "") {
                    e.preventDefault();
                    const hasNational =
                      normalized.replace(/\D/g, "").length >
                      (normalized.startsWith("+49") ? 3 : 1);
                    setAnchorDataValues((prev) => ({
                      ...prev,
                      [template.value]: hasNational ? normalized : "",
                    }));
                  }
                }}
                className={styles.telInput}
                sx={
                  useInheritedValueStyling
                    ? {
                        "& .MuiInputBase-input": {
                          color: "var(--newWork60)",
                          fontStyle: "italic",
                        },
                      }
                    : undefined
                }
              />
            }
          />
        );
      }
      case "subject_id_list":
        return (
          <SubjectSearchSelect
            isMulti={true}
            isClearable={true}
            placeholder={t("search_placeholder")}
            value={Array.isArray(value) ? value : []}
            onChange={(selectedOption: any) => {
              const selected = Array.isArray(selectedOption)
                ? selectedOption
                : selectedOption
                ? [selectedOption]
                : [];
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: selected,
              }));
            }}
            customStyles={inheritedSelectStyles}
            loadOptions={(input: string) =>
              fetchSubjects(input, {
                entity: "subject/quicksearch",
                queryParameter: "users",
                companyIdForFiltering: kaerCompanyId,
              })
            }
          />
        );
      case "wz_code_id_list":
        return (
          <SubjectSearchSelect
            isMulti={true}
            isClearable={true}
            placeholder={t("search_placeholder")}
            value={Array.isArray(value) ? value : []}
            onChange={(selectedOption: any) => {
              const selected = Array.isArray(selectedOption)
                ? selectedOption
                : selectedOption
                ? [selectedOption]
                : [];
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: selected,
              }));
            }}
            customStyles={inheritedSelectStyles}
            loadOptions={(input: string) =>
              fetchSubjects(input, {
                entity: "wzCode",
                mapResponseToOptions: (data: any) =>
                  (data?.results || []).map((item: any) => ({
                    value: item.id,
                    label: `${item.code} - ${item.description}`,
                  })),
              }) || []
            }
          />
        );
      case "object_list": {
        const allowedValueSet = valueMaps[template.value] || [];
        const typeMapping = typeMappings[template.value] || {};
        const objectListData = objectListFields[template.value] || [];

        if (allowedValueSet.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              {t("no_fields_configured")}
            </Typography>
          );
        }

        return (
          <Box>
            {objectListData.map((obj: any, objIndex: number) => (
              <Box key={objIndex} className={styles.objectListEntry}>
                <Box className={styles.objectListEntryHeader}>
                  <Typography variant="subtitle2">
                    {t("entry")} {objIndex + 1}
                  </Typography>
                  <GenericButton
                    variant="icon"
                    color="error"
                    onClick={() => {
                      const newList = objectListData.filter(
                        (_: any, idx: number) => idx !== objIndex,
                      );
                      setObjectListFields((prev) => ({
                        ...prev,
                        [template.value]: newList,
                      }));
                    }}
                  >
                    <DeleteIcon />
                  </GenericButton>
                </Box>
                {allowedValueSet.map((field: any) => {
                  const fieldName =
                    typeof field === "string" ? field : field.value;
                  const fieldLabel =
                    typeof field === "string"
                      ? field
                      : field.label || field.value;
                  const fieldType = typeMapping[fieldName] || "str";
                  const fieldValue = obj?.[fieldName] ?? "";

                  const setFieldValue = (newValue: any) => {
                    const newList = [...objectListData];
                    newList[objIndex] = {
                      ...newList[objIndex],
                      [fieldName]: newValue,
                    };
                    setObjectListFields((prev) => ({
                      ...prev,
                      [template.value]: newList,
                    }));
                  };

                  return (
                    <Box
                      key={fieldName}
                      className={styles.objectListEntryField}
                    >
                      <TextField
                        size="small"
                        value={fieldLabel}
                        disabled
                        className={styles.flex1}
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                            color: "rgba(0, 0, 0, 0.87)",
                            cursor: "not-allowed",
                          },
                        }}
                      />
                      {fieldType === "phone_number" ? (
                        <MuiTelInput
                          placeholder={t("value")}
                          defaultCountry="DE"
                          langOfCountryName="DE"
                          value={String(fieldValue)}
                          onChange={(newValue) => setFieldValue(newValue)}
                          onPaste={(e: React.ClipboardEvent) => {
                            const text =
                              e.clipboardData?.getData?.("text/plain") ??
                              e.clipboardData?.getData?.("text") ??
                              "";
                            const normalized = normalizePastedPhoneNumber(text);
                            if (normalized !== "") {
                              e.preventDefault();
                              const hasNational =
                                normalized.replace(/\D/g, "").length >
                                (normalized.startsWith("+49") ? 3 : 1);
                              setFieldValue(hasNational ? normalized : "");
                            }
                          }}
                          className={styles.flex1}
                        />
                      ) : fieldType === "date" ? (
                        <AnchorDataOverviewDatePicker
                          className={styles.flex1}
                          size="small"
                          value={
                            fieldValue === "" || fieldValue == null
                              ? null
                              : String(fieldValue)
                          }
                          onChange={(newValue) =>
                            setFieldValue(newValue ?? "")
                          }
                        />
                      ) : (
                        <TextField
                          size="small"
                          placeholder={t("value")}
                          value={String(fieldValue)}
                          onChange={(e) => setFieldValue(e.target.value)}
                          type={
                            fieldType === "int"
                              ? "number"
                              : fieldType === "float"
                              ? "number"
                              : fieldType === "email"
                              ? "email"
                              : "text"
                          }
                          inputProps={
                            fieldType === "float" ? { step: "any" } : undefined
                          }
                          multiline={fieldType === "multiline"}
                          rows={fieldType === "multiline" ? 3 : undefined}
                          className={styles.flex1}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
            <Box className={styles.addObjectListEntryButtonWrapper}>
              <GenericButton
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
                className={styles.addObjectListEntryButton}
                onClick={() => {
                  const emptyObj: any = {};
                  allowedValueSet.forEach((field: any) => {
                    const fieldName =
                      typeof field === "string" ? field : field.value;
                    emptyObj[fieldName] = "";
                  });
                  setObjectListFields((prev) => ({
                    ...prev,
                    [template.value]: [
                      ...(prev[template.value] || []),
                      emptyObj,
                    ],
                  }));
                }}
              >
                {t("add_entry")}
              </GenericButton>
            </Box>
          </Box>
        );
      }
      default: {
        const isStringType = type === "str";
        const raw = value !== undefined ? value : initial;
        const stringDisplayValue =
          raw == null ||
          raw === "" ||
          (typeof raw === "number" && Number.isNaN(raw))
            ? ""
            : String(raw);
        return (
          <TextField
            fullWidth
            value={stringDisplayValue}
            inputRef={(el) => {
              inputRefs.current[template.value] = el;
            }}
            onChange={(e) => {
              setAnchorDataValues((prev) => ({
                ...prev,
                [template.value]: e.target.value,
              }));
            }}
            InputProps={
              isStringType
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t("set_to_empty")}>
                          <GenericButton
                            variant="icon"
                            color="iconBase"
                            onClick={() =>
                              setAnchorDataValues((prev) => ({
                                ...prev,
                                [template.value]: "",
                              }))
                            }
                          >
                            <ClearIcon fontSize="small" />
                          </GenericButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
            sx={inheritedInputSx}
            autoFocus
          />
        );
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        setEditModalOpen(false);
        setSelectedRow(null);
        setAnchorDataValues({});
        setValueMaps({});
        setResetToInheritedTemplates({});
        setInheritedResetMeta({});
        setIsSubjectRootOfTree(false);
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={styles.dialogTitle}>
        {t("edit_anchor_data_value")}
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="anchor-data-overview-edit-form"
          onSubmit={handleSubmit}
          noValidate
          className={styles.dialogContent}
        >
          <Box className={styles.dialogContentRowGroup}>
            <Typography>{t("company_name")}</Typography>
            <TextField fullWidth value={selectedRow?.name || ""} disabled />
          </Box>
          {selectedAnchorDataTemplates.filter(Boolean).map((template) => {
            const templateId = template.value;
            const templateType = anchorDataTemplateTypes[templateId] || "str";
            const initialTemplateValue = selectedRow?.[`value_${templateId}`];
            const currentTemplateValue =
              templateType === "object_list"
                ? objectListFields[templateId] ?? initialTemplateValue
                : anchorDataValues[templateId] !== undefined
                ? anchorDataValues[templateId]
                : initialTemplateValue;

            if (templateType === "str") {
              const resetToInherited = !!resetToInheritedTemplates[templateId];
              return (
                <AnchorDataOverviewFieldEditor
                  key={`${template.value}-${
                    resetToInherited ? "locked" : "edit"
                  }`}
                  template={template}
                  templateType={templateType}
                  value={currentTemplateValue}
                  onChange={(v) =>
                    setAnchorDataValues((prev) => ({
                      ...prev,
                      [templateId]: v,
                    }))
                  }
                  row={selectedRow}
                  resetToInherited={resetToInherited}
                  onResetToInheritedChange={(v) =>
                    setResetToInheritedTemplates((prev) => ({
                      ...prev,
                      [templateId]: v,
                    }))
                  }
                  formatInheritedForDisplay={formatInheritedForDisplay}
                  isSameAnchorDataValue={isSameAnchorDataValue}
                  variant="modal"
                  inputRef={(el) => {
                    inputRefs.current[templateId] = el;
                  }}
                >
                  {({ icons, input, useInheritedValueStyling }) => (
                    <Box
                      className={styles.dialogContentRowGroup}
                      ref={registerValidationTarget(templateId)}
                    >
                      <Box className={styles.dialogContentLabelRow}>
                        <Typography
                          sx={{
                            fontStyle: useInheritedValueStyling
                              ? "italic"
                              : "normal",
                          }}
                        >
                          {String(
                            anchorDataTemplateDetails[templateId]?.label ??
                              template.label ??
                              "",
                          )
                            .replace(/\s*\([^)]*\)\s*$/, "")
                            .trim()}
                        </Typography>
                        {icons}
                      </Box>
                      {input}
                    </Box>
                  )}
                </AnchorDataOverviewFieldEditor>
              );
            }

            const isInherited = !!selectedRow?.[`is_inherited_${templateId}`];
            const hasTemplateValueObject =
              selectedRow?.[`anchor_data_id_${templateId}`] != null;
            const inheritedFrom = selectedRow?.[`inherited_from_${templateId}`];
            const resetMeta = inheritedResetMeta[templateId];
            const inheritedValueForReset =
              resetMeta?.inheritedValue ??
              selectedRow?.[`inherited_value_${templateId}`];
            const parentName =
              resetMeta?.parentName ?? inheritedFrom?.name ?? "-";
            const resetToInherited = !!resetToInheritedTemplates[templateId];
            const isAtOriginalInheritedValue =
              isInherited &&
              isSameAnchorDataValue(
                currentTemplateValue,
                initialTemplateValue,
                templateType,
              );
            const isEmptyAndRevertingToNull =
              inheritedValueForReset == null &&
              (templateType === "subject_id_list" ||
                templateType === "wz_code_id_list") &&
              (currentTemplateValue == null ||
                (Array.isArray(currentTemplateValue) &&
                  currentTemplateValue.length === 0));
            const isResetApplied =
              resetToInherited &&
              (isEmptyAndRevertingToNull ||
                isSameAnchorDataValue(
                  currentTemplateValue,
                  inheritedValueForReset,
                  templateType,
                ));
            const showOriginalInheritedInfoState =
              !isSubjectRootOfTree &&
              isAtOriginalInheritedValue &&
              !resetToInherited;
            const showResetOption =
              !isSubjectRootOfTree &&
              (hasTemplateValueObject || !isAtOriginalInheritedValue) &&
              !showOriginalInheritedInfoState;
            const useInheritedValueStyling =
              showOriginalInheritedInfoState || isResetApplied;
            const resetTooltipValue = formatInheritedForDisplay(
              inheritedValueForReset,
              templateType,
            );

            return (
              <Box
                key={`${template.value}-${
                  resetToInherited ? "locked" : "edit"
                }`}
                className={styles.dialogContentRowGroup}
                ref={registerValidationTarget(templateId)}
              >
                <Box className={styles.dialogContentLabelRow}>
                  <Typography
                    sx={{
                      fontStyle: useInheritedValueStyling ? "italic" : "normal",
                    }}
                  >
                    {String(
                      anchorDataTemplateDetails[templateId]?.label ??
                        template.label ??
                        "",
                    )
                      .replace(/\s*\([^)]*\)\s*$/, "")
                      .trim()}
                  </Typography>
                  <Box className={styles.dialogContentLabelRow}>
                    {showOriginalInheritedInfoState && (
                      <Tooltip
                        placement="top"
                        arrow
                        title={
                          <div className={styles.tooltipContent}>
                            <span className={styles.labelinheritedInfo}>
                              {t("inherited_value")}
                              {": "}
                            </span>
                            {formatInheritedForDisplay(
                              initialTemplateValue,
                              templateType,
                            ) || "-"}
                            <br />
                            <span className={styles.labelinheritedInfo}>
                              {t("inherited_from")}:{" "}
                            </span>
                            {parentName}
                          </div>
                        }
                      >
                        <span className={styles.inheritedIcon}>ⓘ</span>
                      </Tooltip>
                    )}
                    {showResetOption &&
                      (isResetApplied ? (
                        <Tooltip
                          title={
                            <div className={styles.tooltipContent}>
                              <span className={styles.labelinheritedInfo}>
                                {t("value_will_be_inherited")}
                              </span>
                              <br />
                              <br />
                              <span className={styles.labelinheritedInfo}>
                                {t("from")}:{" "}
                              </span>
                              {parentName}
                            </div>
                          }
                          placement="top"
                          arrow
                        >
                          <span className={styles.lockResetIcon}>
                            <LockResetIcon fontSize="small" />
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          placement="top"
                          arrow
                          title={
                            <div className={styles.tooltipContent}>
                              <span className={styles.labelinheritedInfo}>
                                {t("inherited_values_click_to_reset")}
                              </span>
                              <br />
                              <br />
                              <span className={styles.labelinheritedInfo}>
                                {t("inherited_value")}:{" "}
                              </span>
                              {resetTooltipValue || "-"}
                              <br />
                              <span className={styles.labelinheritedInfo}>
                                {t("inherited_from")}:{" "}
                              </span>
                              {parentName}
                            </div>
                          }
                        >
                          <span
                            className={styles.replayIcon}
                            onClick={() => {
                              setResetToInheritedTemplates((prev) => ({
                                ...prev,
                                [templateId]: true,
                              }));
                              if (templateType === "subject_id_list") {
                                setAnchorDataValues((prev) => ({
                                  ...prev,
                                  [templateId]:
                                    inheritedValueToSubjectIdListOptions(
                                      inheritedValueForReset,
                                    ),
                                }));
                              } else if (templateType === "wz_code_id_list") {
                                setAnchorDataValues((prev) => ({
                                  ...prev,
                                  [templateId]:
                                    inheritedValueToWzCodeIdListOptions(
                                      inheritedValueForReset,
                                    ),
                                }));
                              } else if (templateType === "object_list") {
                                setObjectListFields((prev) => ({
                                  ...prev,
                                  [templateId]: Array.isArray(
                                    inheritedValueForReset,
                                  )
                                    ? [...inheritedValueForReset]
                                    : [],
                                }));
                              } else {
                                setAnchorDataValues((prev) => ({
                                  ...prev,
                                  [templateId]: inheritedValueForReset,
                                }));
                              }
                            }}
                          >
                            <ReplayIcon fontSize="small" />
                          </span>
                        </Tooltip>
                      ))}
                  </Box>
                </Box>

                {renderValueInput(template, useInheritedValueStyling)}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <GenericButton
          variant="outlined"
          onClick={() => {
            setEditModalOpen(false);
            setSelectedRow(null);
            setAnchorDataValues({});
            setValueMaps({});
            setTypeMappings({});
            setObjectListFields({});
            setFieldErrors({});
            setResetToInheritedTemplates({});
            setInheritedResetMeta({});
            setIsSubjectRootOfTree(false);
          }}
        >
          {t("cancel")}
        </GenericButton>
        <GenericButton
          variant="contained"
          type="submit"
          form="anchor-data-overview-edit-form"
          loading={isSubmitting}
          disabled={
            !selectedRow ||
            selectedAnchorDataTemplates.filter(Boolean).length === 0 ||
            isSubmitting
          }
        >
          {t("save")}
        </GenericButton>
      </DialogActions>
    </Dialog>
  );
};

export default AnchorDataOverviewModal;
